import { createColumnHelper } from "@tanstack/react-table";
import Table from "@components/ui/table";
import { TablePaginationPageJump } from "@components/ui/table-pagination";
import React, { useCallback, useMemo, useState } from "react";
import MetricNameWithTooltip from "@components/MetricNameWithTooltip/MetricNameWithTooltip";
import { useDiscountCodesAll } from "@lib/api-hooks/useDiscountCodesAll";
import { useNewVsReturningState } from "@lib/hooks/url-state/use-filter-state";
import Spinner from "@tracify/ui/spinner";
import useCurrency from "@lib/hooks/use-currency";
import useFormat from "@lib/hooks/use-format";

const PAGE_SIZE = 10;

type TableRow = {
  discount_code: string;
  count: number;
  revenue: number;
  new_customer_rate: number;
  returning_customer_rate: number;
};

const columnHelper = createColumnHelper<TableRow>();

interface DiscountCodesAllPerformanceOverviewProps {
  /** When set, show discount codes for this store only (used in store subtabs). */
  siteId?: string;
  /** Controlled pagination (e.g. from parent store tabs so each tab keeps its own page). */
  pagination?: { pageIndex: number; pageSize: number };
  onPaginationChange?: (
    updaterOrValue:
      | { pageIndex: number; pageSize: number }
      | ((old: { pageIndex: number; pageSize: number }) => {
          pageIndex: number;
          pageSize: number;
        })
  ) => void;
}

function DiscountCodesAllPerformanceOverview({
  siteId,
  pagination: controlledPagination,
  onPaginationChange: controlledOnPaginationChange,
}: DiscountCodesAllPerformanceOverviewProps) {
  const { calculateCurrencyConvertion } = useCurrency();
  const { formatNumber } = useFormat();
  const { newVsReturning } = useNewVsReturningState();

  const [internalPagination, setInternalPagination] = useState({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  });

  const pagination = controlledPagination ?? internalPagination;
  const onPaginationChange = useCallback(
    (
      updaterOrValue:
        | typeof pagination
        | ((old: typeof pagination) => typeof pagination)
    ) => {
      if (controlledOnPaginationChange) {
        controlledOnPaginationChange(updaterOrValue);
      } else {
        setInternalPagination((prev) =>
          typeof updaterOrValue === "function"
            ? updaterOrValue(prev)
            : updaterOrValue
        );
      }
    },
    [controlledOnPaginationChange]
  );

  const offset = pagination.pageIndex * pagination.pageSize;
  const limit = pagination.pageSize;

  const { data, isFetching } = useDiscountCodesAll({
    enabled: !!siteId,
    limit,
    offset,
    siteIds: siteId ? [siteId] : undefined,
    newVsReturning,
  });

  const tableData = useMemo(() => {
    return (data?.items ?? []).map((item) => ({
      discount_code: item.discount_code,
      count: item.count,
      revenue: item.revenue ?? 0,
      new_customer_rate: item.new_customer_rate ?? 0,
      returning_customer_rate: item.returning_customer_rate ?? 0,
    }));
  }, [data?.items]);

  const totalItemCount = data?.totalItemCount ?? 0;
  const totals = data?.totals;

  const columns = useMemo(
    () => [
      columnHelper.accessor("discount_code", {
        sortUndefined: -1,
        enableResizing: true,
        enableSorting: false,
        size: 200,
        header: "Discount Code",
        cell: ({ getValue }) => (
          <p className="truncate font-bold w-full">{getValue()}</p>
        ),
      }),
      columnHelper.accessor("count", {
        sortUndefined: -1,
        sortDescFirst: true,
        enableResizing: false,
        size: 150,
        cell: ({ getValue }) => (
          <span className="tabular-nums w-full block">
            {getValue().toLocaleString()}
          </span>
        ),
        header: () => (
          <span className="w-full block">
            <MetricNameWithTooltip label="Orders" metric="customer_purchase" />
          </span>
        ),
        footer: () => (
          <span className="tabular-nums font-medium w-full block">
            {totals != null
              ? totals.order.toLocaleString()
              : tableData
                  .reduce((sum, row) => sum + row.count, 0)
                  .toLocaleString()}
          </span>
        ),
      }),
      columnHelper.accessor("revenue", {
        sortUndefined: -1,
        sortDescFirst: true,
        enableResizing: false,
        size: 130,
        cell: ({ getValue }) => (
          <span className="tabular-nums w-full block">
            {calculateCurrencyConvertion(getValue() ?? 0, 2)}
          </span>
        ),
        header: () => (
          <span className="w-full block">
            <MetricNameWithTooltip label="Revenue" metric="customer_revenue" />
          </span>
        ),
        footer: () => (
          <span className="tabular-nums font-medium w-full block">
            {calculateCurrencyConvertion(
              totals != null
                ? totals.revenue
                : tableData.reduce((sum, row) => sum + row.revenue, 0),
              2
            )}
          </span>
        ),
      }),
      columnHelper.accessor("new_customer_rate", {
        sortUndefined: -1,
        sortDescFirst: true,
        enableResizing: false,
        size: 220,
        cell: ({ getValue }) => {
          const v = getValue() ?? 0;
          return (
            <span className="tabular-nums w-full block">
              {formatNumber(v * 100, 2)}%
            </span>
          );
        },
        header: () => (
          <span className="w-full block">
            <MetricNameWithTooltip
              label="New Customer Rate"
              metric="new_customer_rate"
            />
          </span>
        ),
      }),
      columnHelper.accessor("returning_customer_rate", {
        sortUndefined: -1,
        sortDescFirst: true,
        enableResizing: false,
        size: 220,
        cell: ({ getValue }) => {
          const v = getValue() ?? 0;
          return (
            <span className="tabular-nums w-full block">
              {formatNumber(v * 100, 2)}%
            </span>
          );
        },
        header: () => (
          <span className="w-full block">
            <MetricNameWithTooltip
              label="Returning Customer Rate"
              metric="returning_customer_rate"
            />
          </span>
        ),
      }),
    ],
    [tableData, totals, calculateCurrencyConvertion, formatNumber]
  );

  let emptyState: React.ReactNode;
  let loadingState: React.ReactNode;

  if (isFetching) {
    loadingState = (
      <div className="relative flex w-[calc(100vw-320px)] justify-center items-center">
        <Spinner className="mr-2" size="large" />
        <p className="text-foreground">Loading your data...</p>
      </div>
    );
  } else if (!data || tableData.length === 0) {
    emptyState = (
      <div className="relative flex w-[calc(100vw-320px)] justify-center items-center">
        <p>No discount code usage found for the selected period.</p>
      </div>
    );
  }

  return (
    <div className="overflow-visible pt-2">
      <Table<TableRow>
        id={`discount-code-all-performance-overview${
          siteId ? `-${siteId}` : ""
        }`}
        columns={columns}
        loadingState={loadingState}
        emptyState={emptyState}
        data={tableData}
        enableResizing
        showFooter={true}
        disableRowsPerPage={true}
        pagination={pagination}
        onPaginationChange={onPaginationChange}
        paginationTotalRows={totalItemCount}
        hidePagination={false}
      >
        <TablePaginationPageJump />
      </Table>
    </div>
  );
}

export default DiscountCodesAllPerformanceOverview;
